/* eslint-disable array-callback-return */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomQuoteLinesModel, CustomQuoteModel, CustomQuoteProductInfo } from "@adl/App/Services/CustomQuotesService";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export type xMyCustomQuoteDetailsType = {
    isLoading: boolean;
    quote: CustomQuoteModel | undefined;
    productsInfo: CustomQuoteProductInfo[];
    isRequestingQuote: boolean;
    addingToCart: string;
    sortBy: string;
    errorLoading: boolean;
    productLinesQtyToUpdate: CustomQuoteLinesModel[];
    editingSortOrder: boolean;
    realTimePricingFailed: boolean;
    realTimeInventoryFailed: boolean;
    prices?: ProductPriceDto[];
    inventories?: ProductInventoryDto[];
    selectedProductToAdd?: ProductModel;
};

const initialState: xMyCustomQuoteDetailsType = {
    isLoading: false,
    quote: undefined,
    productsInfo: [],
    isRequestingQuote: false,
    addingToCart: "",
    errorLoading: false,
    sortBy: "",
    productLinesQtyToUpdate: [],
    editingSortOrder: false,
    realTimePricingFailed: false,
    realTimeInventoryFailed: false,
};

const reducer = {
    "Pages/xMyCustomQuotes/BeginLoadingQuoteDetails": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { sortBy: string },
    ) => {
        draft.isLoading = true;
        draft.sortBy = actions.sortBy;
    },
    "Pages/xMyCustomQuotes/ClearCustomQuoteDetails": (draft: Draft<xMyCustomQuoteDetailsType>) => {
        draft.quote = initialState.quote;
        draft.isLoading = initialState.isLoading;
        draft.productsInfo = initialState.productsInfo;
        draft.errorLoading = initialState.errorLoading;
        draft.sortBy = initialState.sortBy;
        draft.editingSortOrder = initialState.editingSortOrder;
        draft.realTimePricingFailed = initialState.realTimePricingFailed;
        draft.realTimeInventoryFailed = initialState.realTimeInventoryFailed;
    },
    "Pages/xMyCustomQuotes/CompleteLoadingQuoteDetails": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { quote?: CustomQuoteModel; errorLoading?: boolean; skipPricingAndInventory?: boolean },
    ) => {
        draft.isLoading = false;
        if (actions.quote) {
            draft.quote = actions.quote;
            if (!actions.skipPricingAndInventory) {
                // setting Products Info.
                draft.productsInfo = actions.quote.linesModel?.map(line => {
                    return {
                        qtyOrdered: line.qty,
                        productId: line.productResult.id,
                        unitOfMeasure: line.unitOfMeasureDisplay,
                        productDetailPath: line.productResult.canonicalUrl,
                        lineId: line.lineId,
                        quotePrice: line.quotePrice,
                    };
                });
            } else {
                // updating Products Info
                actions.quote.linesModel?.map(line => {
                    const index = draft.productsInfo.findIndex(product => product.lineId === line.lineId);
                    draft.productsInfo[index].qtyOrdered = line.qty;
                    draft.productsInfo[index].productId = line.productResult.id;
                    draft.productsInfo[index].unitOfMeasure = line.unitOfMeasureDisplay;
                    draft.productsInfo[index].productDetailPath = line.productResult.uri;
                    draft.productsInfo[index].lineId = line.lineId;
                    draft.productsInfo[index].quotePrice = line.quotePrice;
                });
            }
        }
        if (actions.errorLoading) {
            draft.errorLoading = actions.errorLoading;
        }
    },
    "Pages/xMyCustomQuotes/SetEditingSortOrder": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { editingSortOrder: boolean },
    ) => {
        if (actions.editingSortOrder !== undefined) {
            draft.editingSortOrder = actions.editingSortOrder;
        }
    },
    "Pages/xMyCustomQuotes/CompleteLoadingPricing": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { realPricingInfo?: ProductPriceDto[]; realTimePricingFailed?: boolean },
    ) => {
        if (actions.realPricingInfo) {
            draft.prices = actions.realPricingInfo;
        }
        if (actions.realTimePricingFailed) {
            draft.realTimePricingFailed = actions.realTimePricingFailed;
        }
        draft.productsInfo?.map(product => {
            product.pricing = draft.prices?.find(pricing => pricing.productId === product.productId);
            product.failedToLoadPricing = draft.realTimePricingFailed;
        });
    },
    "Pages/xMyCustomQuotes/CompleteLoadingInventory": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { realInventoryInfo?: ProductInventoryDto[]; realTimeInventoryFailed?: boolean },
    ) => {
        if (actions.realInventoryInfo) {
            draft.inventories = actions.realInventoryInfo;
        }
        if (actions.realTimeInventoryFailed) {
            draft.realTimeInventoryFailed = actions.realTimeInventoryFailed;
        }

        draft.productsInfo?.map(product => {
            product.inventory = draft.inventories?.find(inventory => inventory.productId === product.productId);
            product.failedToLoadInventory = draft.realTimeInventoryFailed;
        });
    },
    "Pages/xMyCustomQuotes/UpdateQuantity": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { lineId?: string; qtyOrdered?: number },
    ) => {
        const index = draft.productsInfo.findIndex(product => product.lineId === actions.lineId);
        if (index > -1 && actions.qtyOrdered) {
            draft.productsInfo[index].qtyOrdered = actions.qtyOrdered;
        }
    },
    "Pages/xMyCustomQuotes/DispatchProduct": (
        draft: Draft<xMyCustomQuoteDetailsType>,
        actions: { product?: ProductModel },
    ) => {
        if (actions.product) {
            draft.selectedProductToAdd = actions.product;
        }
    },
};

const xMyCustomQuoteDetails = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xMyCustomQuoteDetails = xMyCustomQuoteDetails;

export type xMyCustomQuoteDetailsState = {
    xMyCustomQuoteDetails: xMyCustomQuoteDetailsType;
} & ApplicationState;
